
import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Lesson } from '@/data/lessons';
import { AlertCircle, Upload, FileSpreadsheet, CheckCircle2, XCircle, Search, ArrowRight, Save } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveLesson, getAllLessons } from '@/utils/lessonUtils';
import { toast } from "sonner";
import analytics from '@/utils/analytics';

interface SpreadsheetRow {
  day: number;
  title: string;
  subtitle: string;
  content: string;
  actionStepTitle: string;
  actionStepDescription: string;
  checkInQuestion: string;
  videoId?: string;
  quote?: string;
  quoteAuthor?: string;
  points?: number;
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  data: SpreadsheetRow[];
}

const MassUploadTool = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [mappedLessons, setMappedLessons] = useState<Lesson[]>([]);
  const [currentTab, setCurrentTab] = useState<string>("upload");
  const [searchTerm, setSearchTerm] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetUpload = () => {
    setFile(null);
    setValidationResult(null);
    setMappedLessons([]);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setValidationResult(null);
      setMappedLessons([]);
    }
  };

  const validateSpreadsheetData = (data: any[]): ValidationResult => {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      data: []
    };

    // Check if we have any data
    if (!data || data.length === 0) {
      result.isValid = false;
      result.errors.push("The spreadsheet is empty. Please upload a file with content.");
      return result;
    }

    // Check required columns
    const requiredColumns = ['day', 'title', 'content', 'actionStepTitle', 'actionStepDescription'];
    const firstRow = data[0];

    for (const column of requiredColumns) {
      if (!(column in firstRow)) {
        result.isValid = false;
        result.errors.push(`Required column "${column}" is missing.`);
      }
    }

    if (!result.isValid) return result;

    // Validate each row
    data.forEach((row, index) => {
      const rowNumber = index + 1;
      
      // Convert row to our expected format
      const processedRow: SpreadsheetRow = {
        day: Number(row.day),
        title: String(row.title || ''),
        subtitle: String(row.subtitle || ''),
        content: String(row.content || ''),
        actionStepTitle: String(row.actionStepTitle || ''),
        actionStepDescription: String(row.actionStepDescription || ''),
        checkInQuestion: String(row.checkInQuestion || 'How did you find today\'s lesson?'),
        videoId: row.videoId || '',
        quote: row.quote || '',
        quoteAuthor: row.quoteAuthor || '',
        points: row.points ? Number(row.points) : 10
      };

      // Validate day number
      if (isNaN(processedRow.day) || processedRow.day <= 0) {
        result.isValid = false;
        result.errors.push(`Row ${rowNumber}: Day must be a positive number.`);
      }

      // Validate required fields
      if (!processedRow.title.trim()) {
        result.isValid = false;
        result.errors.push(`Row ${rowNumber}: Title is required.`);
      }

      if (!processedRow.content.trim()) {
        result.isValid = false;
        result.errors.push(`Row ${rowNumber}: Content is required.`);
      }

      if (!processedRow.actionStepTitle.trim()) {
        result.isValid = false;
        result.errors.push(`Row ${rowNumber}: Action Step Title is required.`);
      }

      if (!processedRow.actionStepDescription.trim()) {
        result.isValid = false;
        result.errors.push(`Row ${rowNumber}: Action Step Description is required.`);
      }

      // Add warning for missing optional fields
      if (!processedRow.videoId) {
        result.warnings.push(`Row ${rowNumber}: Video ID is missing.`);
      }

      result.data.push(processedRow);
    });

    return result;
  };

  const processSpreadsheet = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(10);

    try {
      const data = await readFileAsync(file);
      setUploadProgress(40);
      
      const validation = validateSpreadsheetData(data);
      setValidationResult(validation);
      setUploadProgress(70);

      if (validation.isValid) {
        // Map the validated data to lesson objects
        const existingLessons = getAllLessons();
        const mappedLessons = validation.data.map(row => mapRowToLesson(row, existingLessons));
        setMappedLessons(mappedLessons);
        setCurrentTab("preview");
        
        analytics.trackUserAction('admin_upload_spreadsheet', { 
          rows: validation.data.length,
          filename: file.name,
          success: true
        });
      } else {
        analytics.trackUserAction('admin_upload_spreadsheet', { 
          success: false,
          errorCount: validation.errors.length
        });
      }
      
      setUploadProgress(100);
    } catch (error) {
      console.error("Error processing spreadsheet:", error);
      
      setValidationResult({
        isValid: false,
        errors: ["Failed to process the spreadsheet. Please check the file format."],
        warnings: [],
        data: []
      });
      
      analytics.trackUserAction('admin_upload_spreadsheet', { 
        success: false,
        error: 'file_processing_error'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const readFileAsync = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          if (!data) {
            reject(new Error("Failed to read file data"));
            return;
          }
          
          const workbook = XLSX.read(data, { type: 'binary' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => reject(error);
      
      reader.readAsBinaryString(file);
    });
  };

  const mapRowToLesson = (row: SpreadsheetRow, existingLessons: Lesson[]): Lesson => {
    // Check if a lesson with this day already exists
    const existingLesson = existingLessons.find(lesson => lesson.day === row.day);
    
    // Split content by newlines and filter out empty lines
    const contentArray = row.content
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    
    const newLesson: Lesson = {
      day: row.day,
      title: row.title,
      subtitle: row.subtitle || '',
      content: contentArray.length > 0 ? contentArray : [row.content],
      videoId: row.videoId || '',
      quote: row.quote || '',
      quoteAuthor: row.quoteAuthor || '',
      actionStep: {
        title: row.actionStepTitle,
        description: row.actionStepDescription
      },
      checkInQuestion: {
        type: 'text',
        question: row.checkInQuestion || 'How did you find today\'s lesson?'
      },
      points: row.points || 10
    };
    
    // Merge with existing lesson data if available (preserving fields not in the spreadsheet)
    if (existingLesson) {
      return { ...existingLesson, ...newLesson };
    }
    
    return newLesson;
  };

  const handleSaveAll = () => {
    if (mappedLessons.length === 0) return;
    
    try {
      let successCount = 0;
      for (const lesson of mappedLessons) {
        saveLesson(lesson);
        successCount++;
      }
      
      toast.success(`Successfully saved ${successCount} lessons`, {
        description: "All content has been updated in the system."
      });
      
      analytics.trackUserAction('admin_save_bulk_content', { 
        count: successCount
      });
      
      // Reset the form after successful save
      resetUpload();
      setCurrentTab("upload");
    } catch (error) {
      console.error("Error saving lessons:", error);
      toast.error("Failed to save some lessons", {
        description: "Please try again or contact support."
      });
    }
  };

  const filteredLessons = mappedLessons.filter(lesson => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      lesson.day.toString().includes(term) ||
      lesson.title.toLowerCase().includes(term) ||
      lesson.subtitle.toLowerCase().includes(term) ||
      lesson.content.some(c => c.toLowerCase().includes(term))
    );
  });

  return (
    <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="upload">Upload</TabsTrigger>
        <TabsTrigger 
          value="preview" 
          disabled={mappedLessons.length === 0}
        >
          Preview & Confirm
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="upload" className="space-y-4 mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Upload Content Spreadsheet</CardTitle>
            <CardDescription>
              Upload a CSV or Excel file containing your lesson content.
              Make sure the spreadsheet has the required columns.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={handleFileChange}
                className="flex-1"
              />
              <Button 
                onClick={resetUpload} 
                variant="outline"
                disabled={!file || isUploading}
              >
                Clear
              </Button>
            </div>
            
            {file && (
              <div className="flex items-center text-sm text-muted-foreground gap-2">
                <FileSpreadsheet className="h-4 w-4" />
                <span>Selected file: {file.name}</span>
              </div>
            )}
            
            {isUploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Processing file...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="w-full" />
              </div>
            )}
            
            {validationResult && !validationResult.isValid && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Validation Failed</AlertTitle>
                <AlertDescription>
                  <div className="mt-2 text-sm space-y-1">
                    {validationResult.errors.map((error, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{error}</span>
                      </div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}
            
            {validationResult && validationResult.warnings.length > 0 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Warnings</AlertTitle>
                <AlertDescription>
                  <div className="mt-2 text-sm space-y-1">
                    {validationResult.warnings.map((warning, index) => (
                      <div key={index}>{warning}</div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}
            
            {validationResult && validationResult.isValid && (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <AlertTitle className="text-green-700">Validation Successful</AlertTitle>
                <AlertDescription className="text-green-600">
                  Found {validationResult.data.length} valid content entries ready to be uploaded.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
          <CardFooter className="flex justify-between">
            <div>
              {validationResult && validationResult.isValid && (
                <Badge variant="outline" className="bg-green-50 text-green-700">
                  {validationResult.data.length} items
                </Badge>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={resetUpload}
                disabled={!file || isUploading}
              >
                Reset
              </Button>
              <Button
                onClick={processSpreadsheet}
                disabled={!file || isUploading}
              >
                <Upload className="mr-2 h-4 w-4" />
                Process File
              </Button>
            </div>
          </CardFooter>
        </Card>
        
        <div className="bg-muted rounded-lg p-4">
          <h3 className="font-medium mb-2">Required Spreadsheet Columns</h3>
          <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
            <li><strong>day</strong> - The day number for the lesson</li>
            <li><strong>title</strong> - The lesson title</li>
            <li><strong>subtitle</strong> - (Optional) A subtitle or topic</li>
            <li><strong>content</strong> - The main lesson content (can include line breaks)</li>
            <li><strong>actionStepTitle</strong> - Title for the daily action step</li>
            <li><strong>actionStepDescription</strong> - Instructions for the daily action step</li>
            <li><strong>checkInQuestion</strong> - (Optional) The check-in question</li>
            <li><strong>videoId</strong> - (Optional) Video ID for embedded video</li>
            <li><strong>quote</strong> - (Optional) A quote related to the lesson</li>
            <li><strong>quoteAuthor</strong> - (Optional) Author of the quote</li>
            <li><strong>points</strong> - (Optional) Points earned for completing the lesson</li>
          </ul>
        </div>
      </TabsContent>
      
      <TabsContent value="preview" className="space-y-4 mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Preview Content</CardTitle>
            <CardDescription>
              Review the mapped content before saving. You can edit individual items if needed.
            </CardDescription>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search lessons..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Day</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Content</TableHead>
                    <TableHead>Action Step</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLessons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                        {searchTerm ? "No lessons match your search." : "No lessons found in the uploaded file."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLessons.map((lesson) => (
                      <TableRow key={lesson.day}>
                        <TableCell className="font-medium">{lesson.day}</TableCell>
                        <TableCell>
                          <div className="font-medium">{lesson.title}</div>
                          {lesson.subtitle && (
                            <div className="text-xs text-muted-foreground mt-1">{lesson.subtitle}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="max-h-20 overflow-hidden text-ellipsis">
                            {lesson.content[0].substring(0, 100)}
                            {lesson.content[0].length > 100 && "..."}
                            {lesson.content.length > 1 && (
                              <span className="text-xs text-muted-foreground"> 
                                +{lesson.content.length - 1} more paragraphs
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{lesson.actionStep.title}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {lesson.actionStep.description.substring(0, 60)}
                            {lesson.actionStep.description.length > 60 && "..."}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={getAllLessons().some(l => l.day === lesson.day) 
                              ? "bg-yellow-50 text-yellow-700" 
                              : "bg-green-50 text-green-700"
                            }
                          >
                            {getAllLessons().some(l => l.day === lesson.day) ? "Update" : "New"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => setCurrentTab("upload")}
            >
              Back to Upload
            </Button>
            <Button 
              onClick={handleSaveAll}
              disabled={mappedLessons.length === 0}
            >
              <Save className="mr-2 h-4 w-4" />
              Save All Content
            </Button>
          </CardFooter>
        </Card>
        
        <div className="bg-muted rounded-lg p-4">
          <h3 className="font-medium mb-2">Content Summary</h3>
          <div className="text-sm space-y-1 text-muted-foreground">
            <div className="flex justify-between">
              <span>Total items:</span>
              <span>{mappedLessons.length}</span>
            </div>
            <div className="flex justify-between">
              <span>New lessons:</span>
              <span>{mappedLessons.filter(l => !getAllLessons().some(el => el.day === l.day)).length}</span>
            </div>
            <div className="flex justify-between">
              <span>Updates to existing lessons:</span>
              <span>{mappedLessons.filter(l => getAllLessons().some(el => el.day === l.day)).length}</span>
            </div>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
};

export default MassUploadTool;
